import { EmptyGrowPanelText } from "styles/text";
import { useParams } from "react-router-dom";
import { getGroupData } from "firebase-config";
import { useEffect, useState } from "react";
import { Group } from "model/Group/Group";
import { selectUid } from "selectors/User";
import { Filter } from "model/Filter/Filter";
import { GroupHeader } from "components/Group/Header/GroupHeader";
import { GroupOverview } from "components/Group/Tabs/GroupOverview";
import { GroupLeaderboard } from "components/Group/Tabs/GroupLeaderboard";
import { GroupMembers } from "components/Group/Tabs/GroupMembers";
import { Panel } from "components/Panel/Panel";
import { SingleColumnPage } from "pages/PageTemplates";
import { useAppSelector } from "app/hooks";

export type GroupData = {
  group: Group | undefined;
  groupName: string;
  userStatus: string;
};

const mapFunctionResult = (resultData: any): GroupData => {
  return {
    group: resultData.groupData,
    groupName: resultData.groupName,
    userStatus: resultData.userStatus,
  };
};

export const PickemGroupPage = () => {
  const { groupId } = useParams();
  const lowerGroupId = groupId?.toLowerCase();
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [groupLoading, setGroupLoading] = useState<boolean>(true);
  const [groupData, setGroupData] = useState<GroupData | undefined>();
  const uid = useAppSelector(selectUid);

  useEffect(() => {
    getGroupData({ groupId: lowerGroupId })
      .then((result) => {
        setGroupData(mapFunctionResult(result.data));
      })
      .catch((error) => {
        setErrorMessage(error.message);
      })
      .finally(() => setGroupLoading(false));
  }, []);

  const tabOptions = getTabOptions(groupData, uid) ?? [];
  const [selectedTab, setSelectedTab] = useState(tabOptions[0]);
  const tabFilter: Filter<string> = {
    options: tabOptions,
    selectedValue: selectedTab,
    setSelectedValue: setSelectedTab,
  };

  useEffect(() => {
    if (!selectedTab) {
      setSelectedTab(tabOptions[0]);
    }
  }, [tabOptions]);

  return (
    <SingleColumnPage>
      <Panel loading={groupLoading}>
        {!!errorMessage || !groupData ? (
          <EmptyGrowPanelText>{errorMessage}</EmptyGrowPanelText>
        ) : (
          <GroupHeader
            groupData={groupData}
            groupId={lowerGroupId!}
            tabFilter={tabFilter}
          />
        )}
      </Panel>
      {!groupLoading &&
        !errorMessage &&
        !!groupData &&
        getTabContent(tabFilter.selectedValue, lowerGroupId!, groupData)}
    </SingleColumnPage>
  );
};

const getTabOptions = (
  groupData: GroupData | undefined,
  uid: string | undefined
) => {
  if (!groupData) {
    return undefined;
  }

  if (!groupData.group || !uid) {
    return ["Overview"];
  }

  if (groupData.group.memberUids?.includes(uid) ?? false) {
    return ["Overview", "Leaderboard", "Members"];
  }

  return ["Overview"];
};

const getTabContent = (
  selectedTab: string,
  groupId: string,
  groupData: GroupData
) => {
  switch (selectedTab) {
    case "Overview":
      return <GroupOverview groupData={groupData} groupId={groupId} />;
    case "Leaderboard":
      return <GroupLeaderboard memberIds={groupData.group?.memberUids ?? []} />;
    case "Members":
      return (
        !!groupData.group && (
          <GroupMembers group={groupData.group} groupId={groupId} />
        )
      );
    default:
      return <></>;
  }
};
